import { ApiError } from './api.js';
import { config } from '../config.js';
import type {
  TranscriptionListResponse,
  TranscriptionDetailResponse,
  CreateTranscriptionInput,
  CreateTranscriptionResponse,
} from '../types/transcription.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こしAPI専用のfetchラッパ。api.tsのrequestと同じCookie認証パターン。
 * @graph-connects /transcriptions [calls] 文字起こしAPIエンドポイント
 */
async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${config.apiUrl}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...Object.fromEntries(
        options.headers instanceof Headers
          ? options.headers.entries()
          : Object.entries(options.headers ?? {}),
      ),
    },
    credentials: 'include',
  });

  if (response.status === 401) {
    if (window.location.pathname !== '/login') {
      window.location.href = '/login';
    }
    throw new ApiError('Unauthorized', 401, 'UNAUTHORIZED');
  }

  if (!response.ok) {
    const text = await response.text();
    let message = `HTTP ${String(response.status)}`;
    let code: string | undefined;
    if (text) {
      const body = JSON.parse(text) as { error?: { message?: string; code?: string } };
      if (body.error?.message) message = body.error.message;
      if (body.error?.code) code = body.error.code;
    }
    throw new ApiError(message, response.status, code);
  }

  if (response.status === 204) {
    return null as T;
  }

  return response.json() as Promise<T>;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business 文字起こしAPIクライアント。一覧・詳細・作成・削除を提供する。
 * @graph-connects /transcriptions [calls] 文字起こし一覧・作成
 * @graph-connects /transcriptions/{id} [calls] 文字起こし詳細・削除
 */
export const transcriptionApi = {
  list: (): Promise<TranscriptionListResponse> =>
    request<TranscriptionListResponse>('/transcriptions'),
  get: (id: string): Promise<TranscriptionDetailResponse> =>
    request<TranscriptionDetailResponse>(`/transcriptions/${encodeURIComponent(id)}`),
  create: (input: CreateTranscriptionInput): Promise<CreateTranscriptionResponse> =>
    request<CreateTranscriptionResponse>('/transcriptions', {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  remove: (id: string): Promise<{ deleted: boolean }> =>
    request<{ deleted: boolean }>(`/transcriptions/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    }),
  archive: (id: string): Promise<{ archived: boolean }> =>
    request<{ archived: boolean }>(`/transcriptions/${encodeURIComponent(id)}/archive`, {
      method: 'PATCH',
    }),
};
